import React, { useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import {
    Box,
    Typography,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    TextField,
    Button,
    Tabs,
    Tab,
    TableContainer,
    Paper,
    IconButton,
    Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Trash2 as DeleteIcon, ChevronUp, ChevronDown } from 'react-feather';
import Toast from '../../components/Toast';
import { sortRolesByHierarchy } from '../../permissions/roleUtils';
import { buildMatrixColumns } from '../../permissions/pageActionGroups';
import { INFERRED_PAGE_SHELL_APP_VIEW_IDS } from '../../permissions/canAccess';
import {
    useCreateRole,
    useDeleteRole,
    usePermissionMatrixData,
    usePutGrant,
    useReorderRoles,
    useRevokeGrant,
    useRoleUserCounts,
    useUpdateRoleName,
} from '../../hooks/usePermissions';
import RoleUsersDialog from './RoleUsersDialog';

const GROUP_ORDER = ['Company', 'Billing', 'General', 'Super Admin', 'Tabs', 'Other'];

function getRoleUserCount(roleUserCounts, roleId) {
    if (!roleUserCounts || !roleId) {
        return 0;
    }

    const normalizedRoleId = String(roleId).toLowerCase();
    const match = Object.entries(roleUserCounts).find(([key]) => String(key).toLowerCase() === normalizedRoleId);
    return match?.[1] ?? 0;
}

function primaryAccent(theme, opacity) {
    return alpha(theme.palette.primary.main, opacity);
}

const permissionColumnSx = {
    minWidth: 72,
    px: 1,
    verticalAlign: 'top',
    textAlign: 'center',
};

const groupedHeaderSx = {
    minWidth: 144,
    px: 1,
    verticalAlign: 'bottom',
    textAlign: 'center',
};

const pageActionBorderSx = (theme) => `2px solid ${primaryAccent(theme, 0.4)}`;

const pageActionHeaderSx = {
    ...groupedHeaderSx,
    bgcolor: (theme) => primaryAccent(theme, 0.08),
    borderTop: pageActionBorderSx,
    borderLeft: pageActionBorderSx,
    borderRight: pageActionBorderSx,
};

const pageActionSubheaderBaseSx = {
    bgcolor: (theme) => primaryAccent(theme, 0.06),
    fontWeight: 600,
    fontSize: '0.75rem',
    color: (theme) => primaryAccent(theme, 0.9),
};

function pageActionSubheaderSx(side) {
    return {
        ...permissionColumnSx,
        ...pageActionSubheaderBaseSx,
        ...(side === 'start' && { borderLeft: pageActionBorderSx }),
        ...(side === 'end' && { borderRight: pageActionBorderSx }),
    };
}

function pageActionCellSx(side, isLastRow) {
    return {
        ...permissionColumnSx,
        bgcolor: (theme) => primaryAccent(theme, 0.04),
        ...(side === 'start' && { borderLeft: pageActionBorderSx }),
        ...(side === 'end' && { borderRight: pageActionBorderSx }),
        ...(isLastRow && { borderBottom: pageActionBorderSx }),
    };
}

function ViewHeader({ view }) {
    return (
        <Tooltip title={view.description ?? ''}>
            <span>
                {view.name}
                {view.route &&
                    <Typography variant="caption" display="block" color="text.secondary">
                        {view.route}
                    </Typography>
                }
            </span>
        </Tooltip>
    );
}

function PermissionGroupTable({
    columns,
    roles,
    roleUserCounts,
    onRoleNameSave,
    onDeleteRole,
    onMoveRole,
    onShowRoleUsers,
    hasGrant,
    hasPageViewGrant,
    onToggleView,
    onToggleAdmin,
    onToggleSingle,
    grantKey,
}) {
    const hasPageActionGroups = columns.some((column) => column.type === 'pageActions');

    return (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell rowSpan={hasPageActionGroups ? 2 : 1} sx={{ minWidth: 300, fontWeight: 600 }}>
                            Role
                        </TableCell>
                        {columns.map((column) => (
                            column.type === 'pageActions'
                                ? (
                                    <TableCell
                                        key={column.parent.id}
                                        colSpan={2}
                                        sx={pageActionHeaderSx}
                                    >
                                        <ViewHeader view={column.parent} />
                                    </TableCell>
                                )
                                : (
                                    <TableCell key={column.view.id} rowSpan={hasPageActionGroups ? 2 : 1} sx={permissionColumnSx}>
                                        <ViewHeader view={column.view} />
                                    </TableCell>
                                )
                        ))}
                    </TableRow>
                    {hasPageActionGroups &&
                        <TableRow>
                            {columns.flatMap((column) => (
                                column.type === 'pageActions'
                                    ? [
                                        <TableCell
                                            key={`${column.parent.id}-view`}
                                            sx={pageActionSubheaderSx('start')}
                                        >
                                            View
                                        </TableCell>,
                                        <TableCell
                                            key={`${column.parent.id}-admin`}
                                            sx={pageActionSubheaderSx('end')}
                                        >
                                            Admin
                                        </TableCell>,
                                    ]
                                    : []
                            ))}
                        </TableRow>
                    }
                </TableHead>
                <TableBody>
                    {roles.map((role, index) => {
                        const isLastRow = index === roles.length - 1;
                        return (
                        <TableRow key={role.id}>
                            <TableCell sx={{ verticalAlign: 'middle' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                    <Tooltip title="Hierarchy rank (lower number = higher privilege)">
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                minWidth: 28,
                                                textAlign: 'center',
                                                fontWeight: 600,
                                                border: 1,
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                px: 0.5,
                                                py: 0.25,
                                            }}
                                        >
                                            {role.hierarchyOrder ?? index + 1}
                                        </Typography>
                                    </Tooltip>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <IconButton
                                            size="small"
                                            disabled={index === 0}
                                            onClick={() => onMoveRole(role.id, 'up')}
                                            aria-label={`Move ${role.name} up`}
                                        >
                                            <ChevronUp size={14} />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            disabled={index === roles.length - 1}
                                            onClick={() => onMoveRole(role.id, 'down')}
                                            aria-label={`Move ${role.name} down`}
                                        >
                                            <ChevronDown size={14} />
                                        </IconButton>
                                    </Box>
                                    <TextField
                                        key={`${role.id}:${role.name}`}
                                        size="small"
                                        defaultValue={role.name ?? ''}
                                        onBlur={(e) => onRoleNameSave(role.id, e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.target.blur();
                                            }
                                        }}
                                        sx={{ minWidth: 140, flex: 1 }}
                                    />
                                    <Tooltip title="View employees with this role">
                                        <Chip
                                            size="small"
                                            label={`${getRoleUserCount(roleUserCounts, role.id)} users`}
                                            clickable
                                            color="primary"
                                            variant="outlined"
                                            onClick={() => onShowRoleUsers(role)}
                                            sx={{ fontWeight: 600 }}
                                        />
                                    </Tooltip>
                                    <Tooltip title="Delete role">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => onDeleteRole(role)}
                                            aria-label={`Delete ${role.name}`}
                                        >
                                            <DeleteIcon size={16} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </TableCell>
                            {columns.map((column) => {
                                if (column.type === 'pageActions') {
                                    return (
                                        <React.Fragment key={grantKey(role.id, column.parent.id, 'page-actions')}>
                                            <TableCell sx={pageActionCellSx('start', isLastRow)}>
                                                <Checkbox
                                                    size="small"
                                                    sx={{ p: 0.5 }}
                                                    checked={hasPageViewGrant(role.id, column.parent.id, column.admin.id)}
                                                    onChange={() => onToggleView(role.id, column.parent.id, column.admin.id)}
                                                />
                                            </TableCell>
                                            <TableCell sx={pageActionCellSx('end', isLastRow)}>
                                                <Checkbox
                                                    size="small"
                                                    sx={{ p: 0.5 }}
                                                    checked={hasGrant(role.id, column.admin.id)}
                                                    onChange={() => onToggleAdmin(role.id, column.parent.id, column.admin.id)}
                                                />
                                            </TableCell>
                                        </React.Fragment>
                                    );
                                }

                                return (
                                    <TableCell key={grantKey(role.id, column.view.id)} sx={permissionColumnSx}>
                                        <Checkbox
                                            size="small"
                                            sx={{ p: 0.5 }}
                                            checked={hasGrant(role.id, column.view.id)}
                                            onChange={() => onToggleSingle(role.id, column.view.id)}
                                        />
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function PermissionMatrix() {
    const { matrix } = usePermissionMatrixData();
    const { data: roleUserCounts = {} } = useRoleUserCounts();
    const putGrant = usePutGrant();
    const revokeGrantMutation = useRevokeGrant();
    const createRoleMutation = useCreateRole();
    const updateRoleName = useUpdateRoleName();
    const deleteRoleMutation = useDeleteRole();
    const reorderRoles = useReorderRoles();

    const [newRoleName, setNewRoleName] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const [roleUsersDialog, setRoleUsersDialog] = useState({ open: false, role: null });

    function showRoleUsers(role) {
        setRoleUsersDialog({ open: true, role });
    }

    function closeRoleUsersDialog() {
        setRoleUsersDialog({ open: false, role: null });
    }

    function grantKey(roleId, appViewId, suffix = '') {
        return `${roleId}-${appViewId}-${suffix}`;
    }

    function normalizeId(id) {
        return id ? String(id).toLowerCase() : '';
    }

    function hasGrant(roleId, appViewId) {
        return matrix.grants.some((grant) => {
            const grantRoleId = grant.roleId;
            const grantAppViewId = grant.appViewId;
            return normalizeId(grantRoleId) === normalizeId(roleId)
                && normalizeId(grantAppViewId) === normalizeId(appViewId);
        });
    }

    function findGrant(roleId, appViewId) {
        return matrix.grants.find((grant) => {
            const grantRoleId = grant.roleId;
            const grantAppViewId = grant.appViewId;
            return normalizeId(grantRoleId) === normalizeId(roleId)
                && normalizeId(grantAppViewId) === normalizeId(appViewId);
        });
    }

    async function putGrantRequest(roleId, appViewId, conditions = null) {
        await putGrant.mutateAsync({ roleId, appViewId, conditions });
    }

    async function revokeGrant(roleId, appViewId) {
        const existing = findGrant(roleId, appViewId);
        if (!existing) {
            return;
        }
        await revokeGrantMutation.mutateAsync(existing.id);
    }

    async function toggleSingleGrant(roleId, appViewId) {
        if (hasGrant(roleId, appViewId)) {
            await revokeGrant(roleId, appViewId);
        } else {
            await putGrantRequest(roleId, appViewId);
        }
    }

    function hasPageViewGrant(roleId, parentId, adminId) {
        return hasGrant(roleId, parentId) || hasGrant(roleId, adminId);
    }

    async function togglePageViewGrant(roleId, parentId, adminId) {
        if (hasPageViewGrant(roleId, parentId, adminId)) {
            if (hasGrant(roleId, adminId)) {
                await revokeGrant(roleId, adminId);
            }
            if (hasGrant(roleId, parentId)) {
                await revokeGrant(roleId, parentId);
            }
        } else {
            await putGrantRequest(roleId, parentId);
        }
    }

    async function togglePageAdminGrant(roleId, parentId, adminId) {
        if (hasGrant(roleId, adminId)) {
            await revokeGrant(roleId, adminId);
        } else {
            if (!hasGrant(roleId, parentId)) {
                await putGrantRequest(roleId, parentId);
            }
            const existingAdminGrant = findGrant(roleId, adminId);
            const conditions = existingAdminGrant?.conditions ?? null;
            await putGrantRequest(roleId, adminId, conditions);
        }
    }

    async function createRole() {
        await createRoleMutation.mutateAsync(newRoleName);
        setNewRoleName('');
    }

    async function saveRoleName(roleId, nextName) {
        const name = nextName?.trim();
        const original = matrix.roles.find((role) => role.id === roleId)?.name;
        if (!name || name === original) {
            return;
        }

        try {
            await updateRoleName.mutateAsync({ roleId, name });
            Toast.fire({ title: 'Role renamed', icon: 'success' });
            const updatedRole = matrix.roles.find((role) => role.id === roleId);
            if (updatedRole) {
                showRoleUsers({ ...updatedRole, name });
            }
        } catch (error) {
            const message = error.response?.data ?? 'Could not rename role';
            Toast.fire({ title: message, icon: 'error' });
        }
    }

    async function deleteRole(role) {
        const confirmed = await Swal.fire({
            title: `Delete "${role.name}"?`,
            text: 'This removes the role and its permission grants. Roles assigned to users cannot be deleted.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            confirmButtonColor: '#d32f2f',
        });

        if (!confirmed.isConfirmed) {
            return;
        }

        try {
            await deleteRoleMutation.mutateAsync(role.id);
            Toast.fire({ title: 'Role deleted', icon: 'success' });
        } catch (error) {
            const message = error.response?.data ?? 'Could not delete role';
            Toast.fire({ title: message, icon: 'error' });
        }
    }

    const sortedRoles = useMemo(
        () => sortRolesByHierarchy(matrix.roles),
        [matrix.roles]
    );

    async function moveRole(roleId, direction) {
        const roleIds = sortedRoles.map((role) => role.id);
        const index = roleIds.indexOf(roleId);
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        if (index < 0 || swapIndex < 0 || swapIndex >= roleIds.length) {
            return;
        }

        [roleIds[index], roleIds[swapIndex]] = [roleIds[swapIndex], roleIds[index]];

        try {
            await reorderRoles.mutateAsync(roleIds);
        } catch (error) {
            const message = error.response?.data ?? 'Could not reorder roles';
            Toast.fire({ title: message, icon: 'error' });
        }
    }

    const inferredShellIds = useMemo(
        () => new Set(INFERRED_PAGE_SHELL_APP_VIEW_IDS.map((id) => id.toLowerCase())),
        []
    );

    const groupedViews = useMemo(() => {
        const groups = matrix.views
            .filter((view) => !inferredShellIds.has(String(view.id).toLowerCase()))
            .reduce((acc, view) => {
            const group = view.navGroup ?? 'Other';
            if (!acc[group]) {
                acc[group] = [];
            }
            acc[group].push(view);
            return acc;
        }, {});

        return GROUP_ORDER
            .filter((group) => groups[group]?.length)
            .map((group) => [group, groups[group]]);
    }, [matrix.views, inferredShellIds]);

    const safeActiveTab = groupedViews.length > 0
        ? Math.min(activeTab, groupedViews.length - 1)
        : 0;
    const activeGroup = groupedViews[safeActiveTab];

    const activeColumns = useMemo(() => {
        if (!activeGroup) {
            return [];
        }
        return buildMatrixColumns(activeGroup[1]);
    }, [activeGroup]);

    function handleTabChange(_event, value) {
        setActiveTab(value);
    }

    return (
        <Box sx={{ m: '10px', mt: 4 }}>
            <Typography variant="h6">Role Permissions</Typography>
            <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
                Roles are listed top-to-bottom from highest to lowest hierarchy. The bordered number is hierarchy rank (lower = higher privilege).
                Click a user count to see who has that role. Renaming a role opens the same list.
                Lower ranks can assign equal or lower roles on the Employees tab. Botmark super-admins see every role.
                Pages with elevated actions show View and Admin columns; granting Admin also grants View.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
                <TextField
                    label="New role name"
                    value={newRoleName}
                    onChange={(e) => setNewRoleName(e.target.value)}
                    size="small"
                />
                <Button variant="contained" onClick={createRole} disabled={!newRoleName.trim()}>
                    Add role
                </Button>
            </Box>

            {groupedViews.length > 0 &&
                <>
                    <Tabs
                        value={safeActiveTab}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                    >
                        {groupedViews.map(([group]) => (
                            <Tab key={group} label={group} />
                        ))}
                    </Tabs>

                    {activeGroup &&
                        <Box role="tabpanel">
                            <PermissionGroupTable
                                columns={activeColumns}
                                roles={sortedRoles}
                                roleUserCounts={roleUserCounts}
                                onRoleNameSave={saveRoleName}
                                onDeleteRole={deleteRole}
                                onMoveRole={moveRole}
                                onShowRoleUsers={showRoleUsers}
                                hasGrant={hasGrant}
                                hasPageViewGrant={hasPageViewGrant}
                                onToggleView={togglePageViewGrant}
                                onToggleAdmin={togglePageAdminGrant}
                                onToggleSingle={toggleSingleGrant}
                                grantKey={grantKey}
                            />
                        </Box>
                    }
                </>
            }
            <RoleUsersDialog
                open={roleUsersDialog.open}
                role={roleUsersDialog.role}
                onClose={closeRoleUsersDialog}
            />
        </Box>
    );
}

export default PermissionMatrix;
